import { Component, EventEmitter, input, Input, InputSignal, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { toSentenceCase } from '@app/core/functions/toSentenceCase';
import { BigCommerceModeStateService } from '@app/core/services/bigcommerce-mode-state.service';
import { ProductTerminologyService } from '@app/core/services/product-terminology.service';
import { ScheduleStatusService } from '@app/core/services/schedule-status.service';
import { ALL_EXPORTS } from '@app/exports-legacy/models/constants/exports.constants';
import { ExportTypeEnum } from '@app/exports/models/enums/export-type.enum';
import { ExportInterface } from '@app/exports/models/interfaces/export.interface';
import { EDSUtilsService } from '@app/exports/services/eds-utils.service';
import { ExportDestinationService } from '@app/exports/services/export-destination.service';
import { OrderByPipe } from '@app/modules/pipes/pipes/order-by/order-by.pipe';
import { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { Subject, pairwise, startWith, takeUntil } from 'rxjs';

interface SelectExportInterface extends ExportInterface {
    disabled?: boolean;
    description?: string;
    group?: string;
}

@Component({
    selector: 'fdx-export-select',
    templateUrl: './export-select.component.html',
    styleUrl: './export-select.component.scss',
    host: {
        '[class.fdx-export-select--full-width]': 'fullWidth()'
    },
    standalone: false
})
export class ExportSelectComponent implements OnInit, OnChanges, OnDestroy {

    @Input() formGroup: UntypedFormGroup;
    @Input() controlName: string;

    @Input() exports: ExportInterface[];
    @Output() readonly exportsChange: EventEmitter<ExportInterface[]> = new EventEmitter<ExportInterface[]>();
    @Input() triggers?: { export_id: string; }[];
    /** Selected export ids to render as invalid (danger) chips — e.g. an export missing a
     *  field mapping the caller's flow requires. Undefined/empty renders every chip normally. */
    readonly invalidExportIds: InputSignal<ReadonlySet<string> | undefined> =
        input<ReadonlySet<string> | undefined>(undefined);

    @Input() multiple?: boolean = false;
    @Input() multipleWrap?: boolean = true;
    @Input() placeholder?: string;
    @Input() allowBlank?: boolean = false;
    @Input() filterDestinationExports?: boolean = false;
    @Input() prefix?: string = null;
    @Input() tabIndex?: number = null;
    @Input() isEBSDatabase: boolean = false;
    @Input() isEDSv2Database: boolean = false;
    @Input() grouped?: boolean = false;
    @Input() displayDeactivated?: boolean = false;
    /** When false, omits the synthetic "All exports" / General row (id `0`). */
    @Input() includeAllExportsOption?: boolean = true;
    /** When true, omits the inner "Exports:" prefix in the multi-select placeholder. */
    @Input() hidePlaceholderExportPrefix?: boolean = false;
    /** When true, stretches the ng-select control and dropdown panel to 100% width. */
    readonly fullWidth: InputSignal<boolean> = input<boolean>(false);
    /** Caps the number of selectable items in a multi-select. Undefined means no cap. */
    readonly maxSelectedItems: InputSignal<number | undefined> = input<number | undefined>(undefined);

    orderedExports: SelectExportInterface[] = [];

    private exportsWithTriggers?: Set<string>;

    private readonly unsubscribe$: Subject<void> = new Subject<void>();

    get control(): UntypedFormControl {
        return this.formGroup?.controls[this.controlName] as UntypedFormControl;
    }

    get showNumSelected(): boolean {
        return this.multiple && !this.multipleWrap && this.control?.value?.length > 1;
    }

    get disabled(): boolean {
        return this.control?.disabled;
    }

    get allExportsName(): string {
        return this.bigCommerceMode.showRestrictedUI() ? toSentenceCase(this.productTerminologyService.generalExportText) : 'All exports';
    }

    get allExports(): ExportInterface {
        return {
            ...ALL_EXPORTS,
            name: this.allExportsName
        }
    }

    constructor(
        protected readonly bigCommerceMode: BigCommerceModeStateService,
        protected readonly edsUtilsService: EDSUtilsService,
        protected readonly exportDestinationService: ExportDestinationService,
        protected readonly productTerminologyService: ProductTerminologyService,
        private readonly orderByPipe: OrderByPipe,
        private readonly scheduleStatusService: ScheduleStatusService
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.exports) {
            if (this.multiple && this.includeAllExportsOption) {
                this.addContainsAll();
            }
            if (this.exports) {
                const selectExports: SelectExportInterface[] = this.exports;

                // Sort the exports into groups and add a description to the 'all exports' option
                if (this.grouped) {
                    const index = this.indexOfContainsAll(this.exports);
                    selectExports.forEach((e, i) => {
                        if (i === index) {
                            e.description = 'Your unified product data for all channels.';
                        } else {
                            e.group = toSentenceCase(this.productTerminologyService.exportGroupText);
                        }
                    });
                }

                if (this.filterDestinationExports) {
                    // Ideally we'd use map, but it resets the object which changes the index in the ng-select
                    selectExports.forEach((exprt: SelectExportInterface) => {
                        exprt.disabled = exprt.export_type === ExportTypeEnum.Destination;
                    });
                }

                this.orderedExports = this.orderByPipe.transform(selectExports, ['id == 0', 'name']);
                if (this.displayDeactivated) {
                    this.sortDeactivatedExportsLast(this.orderedExports);
                }
            }
        }

        if (changes.triggers) {
            if (this.triggers) {
                this.exportsWithTriggers = new Set(
                    this.triggers.map(
                        (t) => t.export_id
                    )
                );
            } else {
                this.exportsWithTriggers = undefined;
            }
        }
    }

    ngOnInit(): void {
        if (this.multiple && !this.allowBlank) {
            this.initFallbackValue();
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    initFallbackValue(): void {
        this.formGroup.controls[this.controlName].valueChanges.pipe(
            startWith(this.formGroup.controls[this.controlName].value),
            pairwise(),
            takeUntil(this.unsubscribe$)
        ).subscribe(
            ([prev, next]: [ExportInterface[], ExportInterface[]]) => {
                const indexOfContainsAll: number = this.indexOfContainsAll(this.orderedExports);
                const fallbackValue: SelectExportInterface[] = indexOfContainsAll !== -1 ? [this.orderedExports[indexOfContainsAll]] : [this.allExports];

                const nextContainsAllIndex: number = this.indexOfContainsAll(next);
                if (nextContainsAllIndex !== -1) {
                    if (prev && this.indexOfContainsAll(prev) === -1) {
                        this.patchControlValue(fallbackValue);
                    } else {
                        next.splice(nextContainsAllIndex, 1);
                        this.patchControlValue(next);
                    }
                }
                if (!next || next.length === 0) {
                    this.patchControlValue(fallbackValue, true);
                }
            }
        );
    }

    patchControlValue(array: SelectExportInterface[], emit: boolean = false): void {
        this.formGroup.patchValue({
            [this.controlName]: array
        }, { emitEvent: emit });
    }

    get exportsDropdownPlaceholder(): string {
        const exportWord = this.productTerminologyService.exportText;

        if (this.exports?.length > 0) {
            if (this.placeholder) {
                return this.placeholder;
            }
            return this.multiple ? `Choose ${exportWord}s` : `Choose ${exportWord === 'export' ? `an ${exportWord}` : `a ${exportWord}`}`;
        }
        return `This database has no ${exportWord} yet`;
    }

    indexOfContainsAll(array: ExportInterface[]): number {
        let allExportsIndex = -1;

        if (!array) {
            return allExportsIndex;
        }

        for (let i = 0; i < array.length; i++) {
            const exprt = array[i];

            if (exprt.id === '0') {
                allExportsIndex = i;
                break;
            }
        }

        return allExportsIndex;
    }

    addContainsAll(): void {
        if (this.exports && this.indexOfContainsAll(this.exports) === -1) {
            this.exports?.unshift(this.allExports);
            this.exportsChange.emit(this.exports);
        }
    }

    exportHasSchedule(exprt: ExportInterface): boolean {
        return Boolean(exprt.cron);
    }

    exportHasFtpTrigger(exprt: ExportInterface): boolean {
        if (exprt && this.exportsWithTriggers) {
            return this.exportsWithTriggers.has(exprt.id);
        }

        return false;
    }

    exportIsPaused(exprt: ExportInterface): boolean {
        return exprt.paused === '1';
    }

    getExportIcon(exprt: ExportInterface): IconDefinition {
        return this.scheduleStatusService.getStatusIcon(
            this.exportHasSchedule(exprt),
            this.exportHasFtpTrigger(exprt),
            this.exportIsPaused(exprt)
        );
    }

    getExportIconClass(exprt: ExportInterface): string {
        return this.scheduleStatusService.getStatusIconClass(
            this.exportHasSchedule(exprt),
            this.exportHasFtpTrigger(exprt),
            this.exportIsPaused(exprt)
        );
    }

    protected exportSearch(term: string, exprt: ExportInterface): boolean {
        const normalized = term.toLowerCase();
        return exprt.name.toLowerCase().includes(normalized);
    }

    protected trackExport(exprt: ExportInterface): string {
        return exprt.id;
    }

    protected isInvalidExport(exprt: ExportInterface): boolean {
        return this.invalidExportIds()?.has(exprt.id) ?? false;
    }

    protected edrtsIcon(exprt: ExportInterface): string {
        if (exprt.export_triggered) {
            return '/dist/app/assets/icons/edrts_contrast.svg';
        }
        return '/dist/app/assets/icons/edrts_grayscale.svg';
    }

    protected edrtsV2Icon(exprt: ExportInterface): string {
        if (exprt.export_triggered) {
            return '/dist/app/assets/icons/edrtsv2_contrast.svg';
        }
        return '/dist/app/assets/icons/edrtsv2_grayscale.svg';
    }


    protected exportIsDeactivated(exprt: ExportInterface): boolean {
        return this.displayDeactivated && Boolean(exprt.deactivated_at);
    }

    // After orderBy (id 0 + name): keep "All exports" first, then active, then deactivated_at.
    private sortDeactivatedExportsLast(exports: SelectExportInterface[]): void {
        exports.sort((a, b) => {
            if (a.id === '0') {
                return -1;
            }
            if (b.id === '0') {
                return 1;
            }
            return Number(Boolean(a.deactivated_at)) - Number(Boolean(b.deactivated_at));
        });
    }
}
